import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from "@simplewebauthn/browser";
import { api } from "@/api/client";

// The platform ingress rewrites the Origin header, so we always send the real
// page origin explicitly; the backend validates it against a trusted allowlist.
const origin = () => window.location.origin;

export const passkeySupported = () => browserSupportsWebAuthn();
export const platformBiometricAvailable = async () => {
  try { return await platformAuthenticatorIsAvailable(); } catch { return false; }
};

// Enroll a new passkey (fingerprint / Face ID / security key) for the logged-in user.
export async function enrollPasskey() {
  const { data: optionsJSON } = await api.post("/webauthn/registration/options", { origin: origin() });
  const credential = await startRegistration({ optionsJSON });
  const { data } = await api.post("/webauthn/registration/verify", { origin: origin(), credential });
  return data;
}

// Passwordless login. `username` optional — omit for a discoverable-credential prompt.
export async function loginWithPasskey(username) {
  const { data: optionsJSON } = await api.post("/webauthn/login/options", { origin: origin(), username: username || undefined });
  const credential = await startAuthentication({ optionsJSON });
  const { data } = await api.post("/webauthn/login/verify", { origin: origin(), credential });
  return data; // { access_token, user }
}

// Biometric step-up to unlock the secure vault (uses an already-enrolled passkey).
export async function vaultUnlockPasskey() {
  const { data: optionsJSON } = await api.post("/webauthn/vault/options", { origin: origin() });
  const credential = await startAuthentication({ optionsJSON });
  const { data } = await api.post("/webauthn/vault/verify", { origin: origin(), credential });
  return data; // { ok, passed: "biometric" }
}

export const listPasskeys = () => api.get("/webauthn/credentials").then((r) => r.data);
export const deletePasskey = (credential_id) =>
  api.post("/webauthn/credentials/delete", { credential_id }).then((r) => r.data);

// Normalize the browser cancel/error into a friendly message.
export function passkeyErrorMessage(err) {
  const name = err?.name || "";
  if (name === "NotAllowedError") return "Passkey prompt was cancelled or timed out";
  if (name === "InvalidStateError") return "This device already has a passkey for this account";
  if (err?.response?.data?.detail) return err.response.data.detail;
  return err?.message || "Passkey failed";
}
